import type { ComponentType, SVGProps } from 'react'

type FlagProps = SVGProps<SVGSVGElement>

// Flat flags on a shared 640×480 viewBox so they line up at any size. Codes are ISO 3166-1 alpha-2, matching
// the `value` returned by the getCountries endpoint.

// Unit star (outer radius 1, pointing up) scaled by `r` and translated to (cx, cy).
const STAR_POINTS: [number, number][] = [
  [0, -1],
  [0.224, -0.309],
  [0.951, -0.309],
  [0.363, 0.118],
  [0.588, 0.809],
  [0, 0.382],
  [-0.588, 0.809],
  [-0.363, 0.118],
  [-0.951, -0.309],
  [-0.224, -0.309],
]

function Star({ cx, cy, r, fill = '#fff' }: { cx: number; cy: number; r: number; fill?: string }) {
  const d =
    STAR_POINTS.map(([x, y], i) => `${i ? 'L' : 'M'}${(cx + x * r).toFixed(2)} ${(cy + y * r).toFixed(2)}`).join(' ') +
    'Z'
  return <path fill={fill} d={d} />
}

export function SpainFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#AA151B" d="M0 0h640v480H0z" />
      <path fill="#F1BF00" d="M0 120h640v240H0z" />
    </svg>
  )
}

export function UKFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#012169" d="M0 0h640v480H0z" />
      <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 82 480H0v-60l239-178L0 64V0z" />
      <path
        fill="#C8102E"
        d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z"
      />
      <path fill="#FFF" d="M241 0v480h160V0zM0 160v160h640V160z" />
      <path fill="#C8102E" d="M0 193v96h640v-96zM273 0v480h96V0z" />
    </svg>
  )
}

export function CatalanFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#FCDD09" d="M0 0h640v480H0z" />
      <path stroke="#DA121A" strokeWidth="53.3" d="M0 53.3h640M0 160h640M0 266.7h640M0 373.3h640" />
    </svg>
  )
}

export function BrazilFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#009B3A" d="M0 0h640v480H0z" />
      <path fill="#FEDF00" d="m320 39.4 282.7 200.7L320 440.6 37.3 240z" />
      <circle cx="320" cy="240" r="100" fill="#002776" />
      <path fill="#fff" d="M225 270c45-22 95-22 140-5 45 17 80 12 100-2-20 30-65 45-110 30-45-15-90-18-130-23z" />
    </svg>
  )
}

function ItalyFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <g fillRule="evenodd" strokeWidth="1pt">
        <path fill="#fff" d="M0 0h640v480H0z" />
        <path fill="#009246" d="M0 0h213.3v480H0z" />
        <path fill="#CE2B37" d="M426.7 0H640v480H426.7z" />
      </g>
    </svg>
  )
}

function ArgentinaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#74acdf" d="M0 0h640v480H0z" />
      <path fill="#fff" d="M0 160h640v160H0z" />
      <circle cx="320" cy="240" r="42" fill="#f6b40e" stroke="#85340a" strokeWidth="3" />
    </svg>
  )
}

function BoliviaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#d52b1e" d="M0 0h640v160H0z" />
      <path fill="#f9e300" d="M0 160h640v160H0z" />
      <path fill="#007934" d="M0 320h640v160H0z" />
    </svg>
  )
}

function ChileFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#fff" d="M0 0h640v240H0z" />
      <path fill="#d52b1e" d="M0 240h640v240H0z" />
      <path fill="#0039a6" d="M0 0h240v240H0z" />
      <Star cx={120} cy={120} r={66} fill="#fff" />
    </svg>
  )
}

function ColombiaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#fcd116" d="M0 0h640v240H0z" />
      <path fill="#003893" d="M0 240h640v120H0z" />
      <path fill="#ce1126" d="M0 360h640v120H0z" />
    </svg>
  )
}

function CostaRicaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#002b7f" d="M0 0h640v480H0z" />
      <path fill="#fff" d="M0 80h640v320H0z" />
      <path fill="#ce1126" d="M0 160h640v160H0z" />
    </svg>
  )
}

function EcuadorFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#ffdd00" d="M0 0h640v240H0z" />
      <path fill="#034ea2" d="M0 240h640v120H0z" />
      <path fill="#ed1c24" d="M0 360h640v120H0z" />
    </svg>
  )
}

function USAFlag(props: FlagProps) {
  const stripeH = 480 / 13
  const cantonW = 256
  const cantonH = stripeH * 7
  const whiteStripes = [1, 3, 5, 7, 9, 11].map((i) => `M0 ${(i * stripeH).toFixed(2)}h640v${stripeH.toFixed(2)}H0z`)
  const stars = []
  for (let row = 0; row < 9; row++) {
    const offset = row % 2 === 1
    const cols = offset ? 5 : 6
    for (let col = 0; col < cols; col++) {
      const x = (cantonW / 12) * (offset ? 2 + col * 2 : 1 + col * 2)
      const y = (cantonH / 10) * (1 + row)
      stars.push(<Star key={`${row}-${col}`} cx={x} cy={y} r={8} fill="#fff" />)
    }
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#b22234" d="M0 0h640v480H0z" />
      <path fill="#fff" d={whiteStripes.join('')} />
      <path fill="#3c3b6e" d={`M0 0h${cantonW}v${cantonH.toFixed(2)}H0z`} />
      {stars}
    </svg>
  )
}

function GuatemalaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#4997d0" d="M0 0h640v480H0z" />
      <path fill="#fff" d="M213.3 0h213.3v480H213.3z" />
    </svg>
  )
}

function MexicoFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#006847" d="M0 0h213.3v480H0z" />
      <path fill="#fff" d="M213.3 0h213.3v480H213.3z" />
      <path fill="#ce1126" d="M426.7 0h213.3v480H426.7z" />
    </svg>
  )
}

function PanamaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#fff" d="M0 0h640v480H0z" />
      <path fill="#da121a" d="M320 0h320v240H320z" />
      <path fill="#072357" d="M0 240h320v240H0z" />
      <Star cx={160} cy={120} r={58} fill="#072357" />
      <Star cx={480} cy={360} r={58} fill="#da121a" />
    </svg>
  )
}

function ParaguayFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#d52b1e" d="M0 0h640v160H0z" />
      <path fill="#fff" d="M0 160h640v160H0z" />
      <path fill="#0038a8" d="M0 320h640v160H0z" />
    </svg>
  )
}

function PeruFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#d91023" d="M0 0h640v480H0z" />
      <path fill="#fff" d="M213.3 0h213.3v480H213.3z" />
    </svg>
  )
}

function PortugalFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#f00" d="M0 0h640v480H0z" />
      <path fill="#060" d="M0 0h256v480H0z" />
      <circle cx="256" cy="240" r="58" fill="#fff" stroke="#ff0" strokeWidth="8" />
      <circle cx="256" cy="240" r="30" fill="#c00" />
    </svg>
  )
}

function PuertoRicoFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#ed0000" d="M0 0h640v480H0z" />
      <path fill="#fff" d="M0 96h640v96H0zM0 288h640v96H0z" />
      <path fill="#0050f0" d="M0 0 416 240 0 480z" />
      <Star cx={138} cy={240} r={72} fill="#fff" />
    </svg>
  )
}

function DominicanRepublicFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#002d62" d="M0 0h640v480H0z" />
      <path fill="#ce1126" d="M360 0h280v200H360z" />
      <path fill="#ce1126" d="M0 280h280v200H0z" />
      <path fill="#fff" d="M280 0h80v480h-80zM0 200h640v80H0z" />
    </svg>
  )
}

function SouthAfricaFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#de3831" d="M0 0h640v240H0z" />
      <path fill="#002395" d="M0 240h640v240H0z" />
      <g fill="none" strokeLinejoin="round">
        <path stroke="#fff" strokeWidth="172" d="M-30 -30 280 240 -30 510M280 240H665" />
        <path stroke="#007a4d" strokeWidth="104" d="M-30 -30 280 240 -30 510M280 240H665" />
      </g>
      <path fill="#ffb915" d="M-30 -30 250 240 -30 510z" />
      <path fill="#000" d="M-30 12 214 240 -30 468z" />
    </svg>
  )
}

function UruguayFlag(props: FlagProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#fff" d="M0 0h640v480H0z" />
      <path fill="#0038a8" d="M0 53.3h640v53.3H0zM0 160h640v53.3H0zM0 266.7h640v53.3H0zM0 373.3h640v53.3H0z" />
      <path fill="#fff" d="M0 0h213.3v213.3H0z" />
      <circle cx="106.7" cy="106.7" r="44" fill="#fcd116" stroke="#7b3f00" strokeWidth="3" />
    </svg>
  )
}

function VenezuelaFlag(props: FlagProps) {
  const stars = []
  for (let i = 0; i < 8; i++) {
    const angle = ((215 + i * (110 / 7)) * Math.PI) / 180
    const x = 320 + 120 * Math.cos(angle)
    const y = 240 - 120 * Math.sin(angle)
    // react-doctor-disable-next-line react-doctor/no-array-index-as-key -- fixed decorative stars, never reorder
    stars.push(<Star key={i} cx={x} cy={y} r={13} fill="#fff" />)
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" {...props}>
      <path fill="#ffcc00" d="M0 0h640v160H0z" />
      <path fill="#00247d" d="M0 160h640v160H0z" />
      <path fill="#cf142b" d="M0 320h640v160H0z" />
      {stars}
    </svg>
  )
}

export const COUNTRY_FLAGS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  ES: SpainFlag,
  AR: ArgentinaFlag,
  BO: BoliviaFlag,
  BR: BrazilFlag,
  CL: ChileFlag,
  CO: ColombiaFlag,
  CR: CostaRicaFlag,
  EC: EcuadorFlag,
  US: USAFlag,
  GT: GuatemalaFlag,
  MX: MexicoFlag,
  PA: PanamaFlag,
  PY: ParaguayFlag,
  PE: PeruFlag,
  PT: PortugalFlag,
  PR: PuertoRicoFlag,
  DO: DominicanRepublicFlag,
  ZA: SouthAfricaFlag,
  UY: UruguayFlag,
  VE: VenezuelaFlag,
  GB: UKFlag,
  IT: ItalyFlag,
}
